import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";

const URL = "https://alkoholprovet.se/kunskapsbank/kunskapsprovet-vaxjo";

const faqs = [
  { q: "Var gör jag kunskapsprovet i Växjö?", a: "Växjö kommuns tillståndsenhet under Arbete & välfärd administrerar provet. Provet skrivs på plats i kommunhuset eller annan utsedd lokal efter bokning via e-tjänsten på vaxjo.se." },
  { q: "Hur lång är väntetiden i Växjö?", a: "Normalt 3–5 veckor från ansökan till provtillfälle. Något längre under sommaren och studentinflyttningen i augusti–september." },
  { q: "Vad kostar kunskapsprovet i Växjö?", a: "Ansökningsavgift för stadigvarande tillstånd är ca 9 000–11 000 kr. Omprov 1 500–2 500 kr." },
  { q: "Kan jag göra provet på engelska?", a: "Ja, Växjö kommun erbjuder provet på engelska – relevant med Linnéuniversitetets stora andel internationella studenter." },
  { q: "Hur lång är handläggningstiden för tillstånd i Växjö?", a: "Räkna med 2–4 månader från komplett ansökan till beslut för stadigvarande tillstånd." },
];

const sections = [
  { h2: "Tillståndsenheten i Växjö", body: "Växjö kommuns tillståndsenhet är placerad under förvaltningen Arbete och välfärd och hanterar samtliga serveringstillstånd i kommunen. Som residensstad i Kronobergs län och universitetsstad har Växjö en växande restaurangmarknad. Ansökan sker via e-tjänsten på vaxjo.se med BankID. Tillståndsenheten samordnar yttranden från Polismyndigheten Region Syd, Skatteverket, Räddningstjänsten Östra Kronoberg och Miljö- och hälsoskyddskontoret." },
  { h2: "Ansökan – steg för steg", body: "1. Förbered dokument: bolagsbevis, hyreskontrakt, ritning, finansiering, PBI-uppgifter, verksamhetsbeskrivning.\n2. Lämna in ansökan via vaxjo.se e-tjänst.\n3. Betala ansökningsavgift.\n4. Boka kunskapsprov – väntetid 3–5 veckor.\n5. Skriv provet på utsatt tid (90 min, papper).\n6. Vänta in remissvar från polis, skatteverket m.fl.\n7. Få beslut – normalt 2–4 månader." },
  { h2: "Väntetider och säsong", body: "• Vinter (jan–mars): kortast väntetid.\n• Vår (april–maj): ökat tryck inför sommaren.\n• Sommar: hög belastning, särskilt inför Karl-Oskar-dagarna och andra evenemang.\n• Höst: studentinflyttning ger nya ansökningar för studentpubar och nattklubbar.\n\nPlanera ansökan minst 4 månader innan planerad öppning." },
  { h2: "Kostnader i Växjö", body: "• Stadigvarande tillstånd: 9 000–11 000 kr.\n• Tillfälligt tillstånd: 2 000–6 000 kr.\n• Omprov kunskapsprov: 1 500–2 500 kr.\n• Årlig tillsynsavgift: 2 500–10 000 kr.\n\nAvgifter fastställs av kommunfullmäktige." },
  { h2: "Provdagen i Växjö", body: "Provet skrivs på papper under tystnad. 90 minuter. Giltig legitimation krävs. Mobil och anteckningar är inte tillåtna. Resultat meddelas skriftligt inom 1–2 veckor. Underkänt prov: omprov tidigast efter 14 dagar." },
  { h2: "Tips för Växjö-sökande", body: "Växjös tillståndsenhet uppskattar tydliga ansökningar och en realistisk verksamhetsbeskrivning. Tre vanliga fördröjningar:\n• Ofullständig PBI-redogörelse.\n• Otydlig finansieringsplan.\n• Bristande ritning med oklart utmärkta serveringsytor.\n\nFörbered kunskapsprovet på vår plattform – 800 uppdaterade frågor inklusive 2026 års regler." },
];

function Page() {
  return (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Kunskapsbank", to: "/kunskapsbank" },
        { label: "Kunskapsprovet Växjö" },
      ]}
      title="Kunskapsprovet serveringstillstånd Växjö – guide 2026"
      intro="Allt om kunskapsprovet för serveringstillstånd i Växjö: ansökan, väntetider, kostnader och praktiska tips. Uppdaterat för 2026 års regler."
      sections={sections}
      faqs={faqs}
      lastReviewed="2026-06-15"
      related={{
        title: "Läs vidare",
        links: [
          { to: "/kunskapsbank/kunskapsprovet-jonkoping", label: "Kunskapsprovet Jönköping" },
          { to: "/kunskapsbank/kunskapsprovet-orebro", label: "Kunskapsprovet Örebro" },
          { to: "/kunskapsbank/kunskapsprovet-umea", label: "Kunskapsprovet Umeå" },
          { to: "/kunskapsbank/kunskapsprovet-malmo", label: "Kunskapsprovet Malmö" },
          { to: "/practice", label: "Träna på kunskapsprovet" },
        ],
      }}
      sources={[
        { label: "Växjö kommun – serveringstillstånd", href: "https://www.vaxjo.se/" },
        { label: "Alkohollagen (2010:1622)", href: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/alkohollag-20101622_sfs-2010-1622/" },
      ]}
    />
  );
}

export const Route = createFileRoute("/kunskapsbank/kunskapsprovet-vaxjo")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Kunskapsprovet Växjö – ansökan, väntetid & kostnad 2026" },
      { name: "description", content: "Allt om kunskapsprovet för serveringstillstånd i Växjö: ansökan, väntetider, kostnader och tips. Uppdaterat 2026." },
      { property: "og:title", content: "Kunskapsprovet Växjö – komplett guide 2026" },
      { property: "og:description", content: "Allt om kunskapsprovet för serveringstillstånd i Växjö." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Kunskapsbank", url: "https://alkoholprovet.se/kunskapsbank" },
        { label: "Kunskapsprovet Växjö", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Kunskapsprovet Växjö 2026",
        description: "Komplett guide till kunskapsprovet för serveringstillstånd i Växjö.",
        datePublished: "2026-06-15",
        dateModified: "2026-06-15",
      }),
    ],
  }),
});
